import { ref, readonly } from 'vue'
import { useRoute } from 'vue-router'
import { buildLoginUrl, buildRegisterUrl, getHosts } from '~/shared/utils/env'

export interface AuthModalParams {
  backUrl?: string
  title?: string
  showContinueWithoutAuth?: boolean
  onContinueWithoutAuth?: () => void
  [key: string]: unknown
}

// Глобальное состояние модалки (синглтон)
const isOpen = ref(false)
const modalParams = ref<AuthModalParams>({})
const modalResolve = ref<((value: boolean) => void) | null>(null)

export const useAuthModal = () => {
  const route = useRoute()
  const requestUrl = useRequestURL()
  const config = useRuntimeConfig()
  const hosts = getHosts({
    NUXT_PUBLIC_ENVIRONMENT_STAND: config.public.environmentStand,
  })

  const openModal = (params?: AuthModalParams): Promise<boolean> => {
    modalParams.value = { backUrl: params?.backUrl, ...params }
    isOpen.value = true

    return new Promise(resolve => {
      modalResolve.value = resolve
    })
  }

  const closeModal = () => {
    isOpen.value = false
    modalParams.value = {}

    if (modalResolve.value) {
      modalResolve.value(false)
      modalResolve.value = null
    }
  }

  /** Backurl от origin текущего запроса: возврат на тот же хост после авторизации. */
  const getBackUrl = (url?: string): string => {
    return requestUrl.origin + (url ?? route.fullPath)
  }

  // Получение URL для логина с backurl
  const getLoginUrl = (url?: string): string => buildLoginUrl(hosts, getBackUrl(url))

  // Получение URL для регистрации с backurl
  const getRegisterUrl = (url?: string): string => buildRegisterUrl(hosts, getBackUrl(url))

  // Перейти на указанный тип авторизации
  const redirectToAuth = (mode: 'login' | 'register') => {
    const url =
      mode === 'login'
        ? getLoginUrl(modalParams.value.backUrl)
        : getRegisterUrl(modalParams.value.backUrl)

    window.location.href = url
    closeModal()
  }

  // Отмена (просто закрыть)
  const cancel = () => {
    closeModal()
  }

  const continueWithoutAuth = () => {
    const callback = modalParams.value.onContinueWithoutAuth
    if (typeof callback === 'function') {
      callback()
    }

    if (modalResolve.value) {
      modalResolve.value(true)
      modalResolve.value = null
    }

    isOpen.value = false
    modalParams.value = {}
  }

  return {
    // Состояние
    isOpen: readonly(isOpen),
    params: readonly(modalParams),

    // Методы
    openModal,
    closeModal,
    redirectToAuth,
    cancel,
    continueWithoutAuth,
  }
}
